"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const GTA_STOPS = [
  { name: "Toronto", center: [-79.3832, 43.6532], bearing: -18 },
  { name: "Etobicoke", center: [-79.5676, 43.6205], bearing: 12 },
  { name: "Mississauga", center: [-79.6441, 43.589], bearing: 28 },
  { name: "Oakville", center: [-79.6877, 43.4675], bearing: 42 },
  { name: "Brampton", center: [-79.7624, 43.7315], bearing: -24 },
  { name: "Vaughan", center: [-79.5083, 43.8372], bearing: -38 },
];

const ROUTE_GEOJSON = {
  type: "Feature",
  geometry: {
    type: "LineString",
    coordinates: GTA_STOPS.map((stop) => stop.center),
  },
};

const STOP_GEOJSON = {
  type: "FeatureCollection",
  features: GTA_STOPS.map((stop) => ({
    type: "Feature",
    properties: { name: stop.name },
    geometry: { type: "Point", coordinates: stop.center },
  })),
};

export default function AppraisalNewsMap() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const timerRef = useRef(null);
  const { resolvedTheme } = useTheme();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return undefined;

    mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken || !mapboxgl.supported()) {
      setHasError(true);
      return undefined;
    }

    setHasError(false);
    mapContainer.current.innerHTML = "";

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const routeColour = resolvedTheme === "dark" ? "#60a5fa" : "#1d4ed8";
    const stopColour = resolvedTheme === "dark" ? "#facc15" : "#ca8a04";

    let mapInstance;

    try {
      mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style:
          resolvedTheme === "dark"
            ? "mapbox://styles/mapbox/dark-v11"
            : "mapbox://styles/mapbox/light-v11",
        center: [-79.555, 43.665],
        zoom: isMobile ? 8.35 : 9.05,
        pitch: isMobile ? 36 : 52,
        bearing: -18,
        interactive: false,
        attributionControl: false,
        antialias: true,
      });
    } catch (error) {
      setHasError(true);
      return undefined;
    }

    mapRef.current = mapInstance;
    mapInstance.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      "bottom-right",
    );

    const scheduleStop = (index, isOpening = false) => {
      const stop = GTA_STOPS[index % GTA_STOPS.length];
      const travelDuration = isOpening ? 2600 : 6400;

      mapInstance.flyTo({
        center: stop.center,
        zoom: isMobile ? 10.4 : 11.25,
        pitch: isMobile ? 42 : 52,
        bearing: stop.bearing - 18,
        duration: travelDuration,
        curve: 1.58,
        essential: false,
        easing: (time) => time * (2 - time),
      });

      timerRef.current = window.setTimeout(() => {
        mapInstance.easeTo({
          center: stop.center,
          zoom: isMobile ? 14.45 : 15.4,
          pitch: isMobile ? 58 : 70,
          bearing: stop.bearing,
          duration: 2800,
          essential: false,
          easing: (time) => time * time * (3 - 2 * time),
        });

        timerRef.current = window.setTimeout(
          () => scheduleStop(index + 1),
          5900,
        );
      }, travelDuration + 250);
    };

    mapInstance.on("style.load", () => {
      const style = mapInstance.getStyle();

      style.layers.forEach((layer) => {
        if (layer.type !== "symbol") return;

        const id = layer.id.toLowerCase();
        if (
          id.includes("poi") ||
          id.includes("transit") ||
          id.includes("airport") ||
          id.includes("ferry")
        ) {
          mapInstance.setLayoutProperty(layer.id, "visibility", "none");
        } else if (id.includes("road-label")) {
          mapInstance.setPaintProperty(layer.id, "text-opacity", 0.28);
        }
      });

      const firstLabelLayer = style.layers.find(
        (layer) => layer.type === "symbol" && layer.layout?.["text-field"],
      )?.id;

      if (!mapInstance.getLayer("alpha-gta-buildings")) {
        mapInstance.addLayer(
          {
            id: "alpha-gta-buildings",
            source: "composite",
            "source-layer": "building",
            filter: ["==", "extrude", "true"],
            type: "fill-extrusion",
            minzoom: 10,
            paint: {
              "fill-extrusion-color": [
                "interpolate",
                ["linear"],
                ["get", "height"],
                0,
                resolvedTheme === "dark" ? "#172554" : "#dbeafe",
                22,
                "#1d4ed8",
                65,
                "#3b82f6",
                120,
                "#d4a017",
                190,
                "#facc15",
              ],
              "fill-extrusion-height": ["get", "height"],
              "fill-extrusion-base": ["get", "min_height"],
              "fill-extrusion-opacity": resolvedTheme === "dark" ? 0.72 : 0.58,
              "fill-extrusion-vertical-gradient": true,
            },
          },
          firstLabelLayer,
        );
      }

      mapInstance.setLight({
        anchor: "viewport",
        color: resolvedTheme === "dark" ? "#93c5fd" : "#ffffff",
        intensity: resolvedTheme === "dark" ? 0.48 : 0.7,
        position: [1.2, 210, 45],
      });

      mapInstance.addSource("alpha-gta-route", {
        type: "geojson",
        data: ROUTE_GEOJSON,
      });
      mapInstance.addLayer({
        id: "alpha-gta-route-glow",
        type: "line",
        source: "alpha-gta-route",
        paint: {
          "line-color": routeColour,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            6,
            12,
            12,
          ],
          "line-opacity": 0.2,
          "line-blur": 4,
        },
      });
      mapInstance.addLayer({
        id: "alpha-gta-route-line",
        type: "line",
        source: "alpha-gta-route",
        paint: {
          "line-color": routeColour,
          "line-width": ["interpolate", ["linear"], ["zoom"], 8, 2.2, 12, 5],
          "line-opacity": 0.76,
          "line-blur": 0.2,
        },
      });

      mapInstance.addSource("alpha-gta-stops", {
        type: "geojson",
        data: STOP_GEOJSON,
      });
      mapInstance.addLayer({
        id: "alpha-gta-stop-glow",
        type: "circle",
        source: "alpha-gta-stops",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 9, 12, 16],
          "circle-color": stopColour,
          "circle-opacity": 0.24,
          "circle-blur": 0.72,
        },
      });
      mapInstance.addLayer({
        id: "alpha-gta-stop-points",
        type: "circle",
        source: "alpha-gta-stops",
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            3.5,
            12,
            6,
          ],
          "circle-color": stopColour,
          "circle-stroke-color": resolvedTheme === "dark" ? "#020617" : "#ffffff",
          "circle-stroke-width": 2,
        },
      });

      if (!prefersReducedMotion) {
        timerRef.current = window.setTimeout(
          () => scheduleStop(0, true),
          900,
        );
      }
    });

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      mapInstance.remove();
      mapRef.current = null;
    };
  }, [resolvedTheme]);

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        ref={mapContainer}
        className={`h-full w-full transition-opacity duration-700 ${
          hasError ? "opacity-0" : "opacity-100"
        }`}
      />

    </div>
  );
}
